import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { combineLatest, of, Subscription } from 'rxjs';
import { debounceTime, filter, switchMap, take } from 'rxjs/operators';
import { ChallengeDefinitionEntity, ChallengeTeamEntity, ChallengeTeamMemberEntity, UserFitnessSummaryEntity } from '../../../../meta-data/app-meta-data.service';
import { ChallengeTeamJoinRequestStatusAllItems } from '../../../../meta-data/app-meta-data.service';
import { DwSecurityUserService } from '@devwareapps/devware-cap';
import { CentralDataService } from '../../../streaker-central/services/central-data.service';
import { TeamSummaryService } from '../../../teams/services/team-summary.service';
import { TeamFormService } from '../../../teams/services/team-form.service';
import { summarizeTeam, challengeHasStarted } from '../../../teams/models/team-card.models';

@Component({ selector: 'app-team-snapshot', templateUrl: './team-snapshot.component.html', styleUrls: ['./team-snapshot.component.scss'] })
export class TeamSnapshotComponent implements OnChanges, OnDestroy {
    @Input() team: Partial<ChallengeTeamEntity>;
    @Input() members: ChallengeTeamMemberEntity[] = [];
    @Input() summaries: UserFitnessSummaryEntity[] = [];
    @Input() canEdit = false;
    userChallengeId = 0;
    pending = false;
    canJoin = false;
    definition: ChallengeDefinitionEntity;
    donation: number = null;
    donationLoading = false;
    donationError = '';
    statusLoading = false;
    statusError = '';
    editing = false;
    actionMessage = '';
    private _requests = new Subscription();
    private _actions = new Subscription();
    private _scope = '';
    private _changes = new Subscription();
    constructor(private _data: CentralDataService, private _security: DwSecurityUserService,
        private _summary: TeamSummaryService, private _forms: TeamFormService) {
        this._changes.add(_data.teamChanged$.pipe(filter(change => !!this.team?.ChallengeTeamId &&
            (!change.teamId || change.teamId === this.team.ChallengeTeamId)), debounceTime(0)).subscribe(() => this.refresh()));
    }
    get membership(): { isMember: boolean; isAdmin: boolean; hasPendingRequest: boolean } {
        const member = this.members.find(row => row.UserChallengeId === this.userChallengeId);
        return { isMember: !!member, isAdmin: member?.IsTeamAdmin === true, hasPendingRequest: !member && this.pending };
    }
    get stats() { return summarizeTeam(this.members, this.summaries); }
    get activitiesStarted(): boolean { return challengeHasStarted(this.definition?.ChallengeStartDate); }
    get storeOpen(): boolean {
        return this.definition?.IsCurrent === true && !!this.definition.StoreStartDate && !!this.definition.StoreEndDate &&
            Date.now() >= Date.parse(this.definition.StoreStartDate) && Date.now() <= Date.parse(this.definition.StoreEndDate);
    }
    ngOnChanges(): void {
        const scope = JSON.stringify([this.team?.ChallengeTeamId, this.team?.ChallengeDefinitionId,
            this._security.securityContext?.ApplicationUser?.UserId]);
        if (scope !== this._scope) { this._scope = scope; this.refresh(); }
    }
    refresh(message = ''): void {
        this._requests.unsubscribe(); this._requests = new Subscription();
        this.actionMessage = message; this.userChallengeId = 0; this.pending = false; this.canJoin = false;
        this.definition = null; this.donation = null; this.statusError = ''; this.donationError = '';
        const teamId = this.team?.ChallengeTeamId, challengeId = this.team?.ChallengeDefinitionId;
        if (!teamId || !challengeId) { return; }
        this.statusLoading = true; this.donationLoading = true;
        this._requests.add(this._summary.loadTeamDonation(challengeId, teamId).pipe(take(1)).subscribe(row => {
            this.donation = row && Number.isFinite(row.DonationAmount) && row.DonationAmount >= 0 ? row.DonationAmount : null;
            this.donationLoading = false;
        }, () => { this.donationLoading = false; this.donationError = 'Donations are unavailable right now.'; }));
        const userId = this._security.securityContext?.ApplicationUser?.UserId;
        const enrollment = userId ? this._data.userProfile(userId).pipe(take(1), switchMap(profiles => profiles.length === 1
            ? this._data.enrollment(profiles[0].UserProfileId, challengeId) : of([]))) : of([]);
        this._requests.add(combineLatest([this._data.definitions(), enrollment]).pipe(take(1), switchMap(([definitions, rows]) => {
            this.definition = definitions.find(row => row.ChallengeDefinitionId === challengeId);
            const completed = rows.filter(row => row.IsRegistrationComplete === true);
            this.userChallengeId = completed.length === 1 ? completed[0].UserChallengeId : 0;
            this.canJoin = !!this.userChallengeId && this.definition?.IsCurrent === true;
            return this.userChallengeId ? this._data.joinRequests(this.userChallengeId) : of([]);
        }), take(1)).subscribe(requests => {
            this.pending = requests.some(row => row.ChallengeTeamId === teamId &&
                row.ChallengeTeamJoinRequestStatusId === ChallengeTeamJoinRequestStatusAllItems.Pending);
            this.statusLoading = false;
        }, () => { this.statusLoading = false; this.statusError = 'Your team status is unavailable right now.'; }));
    }
    editTeam(): void {
        if (!this.canEdit || this.editing) { return; }
        this.editing = true;
        this._actions.add(this._forms.edit(this.team.ChallengeTeamId).pipe(take(1)).subscribe(() => {
            this.editing = false;
        }, () => { this.editing = false; this.actionMessage = 'Unable to open team settings. Please try again.'; }));
    }
    ngOnDestroy(): void { this._requests.unsubscribe(); this._actions.unsubscribe(); this._changes.unsubscribe(); }
}
